import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  Box,
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  Button,
  Divider,
  Paper,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Sync as SyncIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  Webhook as WebhookIcon,
  Settings as SystemIcon,
  Clear as ClearIcon,
  MarkEmailRead as MarkReadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

interface NotificationData {
  id: string;
  type: 'webhook' | 'sync' | 'system' | 'alert' | 'error';
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  data?: any;
  userId?: string;
  companyId: string;
  timestamp: Date;
  read?: boolean;
}

interface SyncStatus {
  id: string;
  platform: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  companyId: string;
  timestamp: Date;
}

interface NotificationCenterProps {
  userId?: string;
  companyId: string;
  token?: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  userId = 'user_001',
  companyId = 'comp_001',
  token
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Socket.io connection — disabled until backend socket server is implemented
  useEffect(() => {
    const ENABLE_SOCKET = import.meta.env.VITE_ENABLE_SOCKET === 'true';
    if (!ENABLE_SOCKET) return;

    const backendUrl = import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_API_BASE_URL?.replace(/\/api\/v1$/, '') || 'http://localhost:3002';
    const newSocket = io(backendUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling']
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Connection events
    newSocket.on('connect', () => {
      console.log('🔌 Connected to notification service');
      setConnected(true);
      
      // Authenticate
      newSocket.emit('authenticate', { userId, companyId, token });
      
      // Join company room
      newSocket.emit('join-company', companyId);
      
      // Subscribe to notification types
      newSocket.emit('subscribe', ['webhook', 'sync', 'system', 'alert', 'error']);
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Disconnected from notification service');
      setConnected(false);
    });

    newSocket.on('authenticated', (data) => {
      console.log('✅ Authenticated:', data);
    });

    // Notification events
    newSocket.on('notification', (notification: NotificationData) => {
      console.log('📨 Received notification:', notification);
      
      setNotifications(prev => [{ ...notification, read: false }, ...prev.slice(0, 49)]);
      setUnreadCount(prev => prev + 1);
      
      // Show toast notification
      showToastNotification(notification);
    });

    newSocket.on('sync-status', (status: SyncStatus) => {
      console.log('🔄 Received sync status:', status);
      
      setSyncStatuses(prev => {
        const existing = prev.find(s => s.id === status.id);
        if (existing) {
          return prev.map(s => s.id === status.id ? status : s);
        }
        return [status, ...prev.slice(0, 9)];
      });
      
      // Show toast for important status changes
      if (status.status === 'completed') {
        toast.success(`${status.platform} sync completed`);
      } else if (status.status === 'failed') {
        toast.error(`${status.platform} sync failed`);
      }
    });

    // Connection health
    const pingInterval = setInterval(() => {
      if (newSocket.connected) {
        newSocket.emit('ping');
      }
    }, 30000);

    newSocket.on('pong', () => {
      // Connection is healthy
    });

    return () => {
      clearInterval(pingInterval);
      newSocket.close();
    };
  }, [userId, companyId, token]);

  const showToastNotification = (notification: NotificationData) => {
    const message = `${notification.title}: ${notification.message}`;
    
    switch (notification.severity) {
      case 'success':
        toast.success(message);
        break;
      case 'warning':
        toast.warning(message);
        break;
      case 'error':
        toast.error(message);
        break;
      default:
        toast.info(message);
    }
  };

  const getNotificationIcon = (type: string, severity: string) => {
    switch (type) {
      case 'webhook':
        return <WebhookIcon />;
      case 'sync':
        return <SyncIcon />;
      case 'system':
        return <SystemIcon />;
      case 'alert':
        return severity === 'error' ? <ErrorIcon /> : <WarningIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const getSyncStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const clearSyncStatuses = () => {
    setSyncStatuses([]);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={connected ? 'Real-time notifications active' : 'Connecting...'}>
        <IconButton
          onClick={handleClick}
          size="large"
          sx={{
            color: connected ? 'inherit' : 'action.disabled'
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Notifications
              {unreadCount > 0 && (
                <Chip 
                  label={unreadCount} 
                  size="small" 
                  color="error" 
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Box>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton size="small" onClick={markAllAsRead}>
                    <MarkReadIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Clear all">
                <IconButton size="small" onClick={clearNotifications}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Connection Status */}
          <Box sx={{ mb: 2 }}>
            <Chip
              icon={<div style={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                backgroundColor: connected ? '#4caf50' : '#f44336' 
              }} />}
              label={connected ? 'Connected' : 'Disconnected'}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Sync Statuses */}
          {syncStatuses.length > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Sync Status
                </Typography>
                <IconButton size="small" onClick={clearSyncStatuses}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
              <Paper variant="outlined" sx={{ mb: 2, maxHeight: 120, overflow: 'auto' }}>
                <List dense>
                  {syncStatuses.map((status) => (
                    <ListItem key={status.id}>
                      <ListItemIcon>
                        <SyncIcon color={getSeverityColor(getSyncStatusColor(status.status)) as any} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${status.platform} - ${status.status}`}
                        secondary={status.message}
                      />
                      <Chip
                        label={status.status}
                        size="small"
                        color={getSeverityColor(getSyncStatusColor(status.status)) as any}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </>
          )}

          {/* Notifications List */}
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
            Recent Notifications
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
            {notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="textSecondary">
                  No notifications yet
                </Typography>
              </Box>
            ) : (
              <List>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem 
                      sx={{ 
                        bgcolor: notification.read ? 'transparent' : 'action.hover',
                        cursor: 'pointer'
                      }}
                      onClick={() => markAsRead(notification.id)}
                    >
                      <ListItemIcon>
                        {getNotificationIcon(notification.type, notification.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" fontWeight={notification.read ? 'normal' : 'bold'}>
                              {notification.title}
                            </Typography>
                            <Chip
                              label={notification.type}
                              size="small"
                              color={getSeverityColor(notification.severity) as any}
                              variant={notification.read ? 'outlined' : 'filled'}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(notification.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCenter; 